// The URL → view-key contract for the top-level router (`PrdApp`).
//
// Lives in its own module rather than beside the component because it is a pure function of the
// URL with no React in it, and because BOTH of PrdApp's dispatch ladders — the mount-time read
// and the `popstate` handler — must resolve a URL identically. Two copies of this ladder is how
// a fresh load and a Back button came to disagree.

/**
 * Top-level paths that name a view, mapped to the SAME key `?view=` uses.
 *
 * Deliberately an allowlist, and deliberately missing the key `clear` that `?view=` accepts: the
 * single destructive purge-everything surface. It should not become reachable by typing a path.
 *
 * ➕ 2026-10-25: a second key, `clear_registry` (WI-54 / ruling `R-Q33`), stood beside `clear` for the
 * model-registry clear. The operator's one-action ruling FULL-DELETED it with its screen.
 *
 * ⚠️ AMENDED 2026-08-29 (ruling R-Q15): this named TWO such keys. The second was `access-expired`
 * — "a terminal error page that is meaningless without its `reason` code and is always reached by
 * an explicit `window.location.assign`" — and it is FULL-DELETED with the magic-link plane, so it
 * is now absent from `?view=` too rather than merely absent from this allowlist.
 *
 * ⛔ AMENDED 2026-09-08 (operator ruling `R-C3`): a `toolbox` row headed this map and is FULL-DELETED
 * — like `access-expired`, absent from `?view=` too rather than merely de-allowlisted. Toolbox runs
 * are now model-scoped, so a mount with no model in scope could only produce `model_id_required`
 * refusals; the surviving entry point is the model homepage's Toolbox tab, reached by
 * `?view=model&model=<id>&tab=toolbox`, which the `model` branch below already carries.
 */
const VIEW_KEY_BY_PATH: Readonly<Record<string, string>> = {
  analytics: 'analytics',
  models: 'models',
  register: 'register',
  dash: 'dash',        // read-only alias for `analytics` — see the read ladder
  // ⛔ `model` IS DELIBERATELY ABSENT, and for the same reason `clear` is:
  // a bare path cannot name it. The model homepage requires a model id (`?model=mdl_…`), so
  // `/model` resolves to a view that has nothing to render. `?view=model&model=<id>` is the only
  // form, and the read ladders refuse the id-less one rather than landing the user on a blank page.
};

/**
 * The view key this URL names.
 *
 * `?view=` stays the canonical WRITTEN form — `setUrlView` still emits it, and it always wins
 * here. The path is consulted only as the fallback, because it previously was not consulted at
 * ALL: dispatch read `window.location.search` and nothing else. So `/models` — a real URL the
 * SPA fallback serves with a 200 (`staticwebapp.config.json` navigationFallback, `nginx.conf`
 * `try_files`), and the URL `postLoginReturnTarget` deliberately returns the user to after SSO
 * — matched no branch and fell through to the default Projects view, leaving the address bar
 * saying one thing and the page (plus the nav highlight, which is derived from the view) saying
 * another. (⚠️ `/toolbox` was this paragraph's worked example until 2026-09-08; ruling `R-C3`
 * full-deleted that route, so it is restated on a path that still resolves.)
 *
 * ⚠️ THE HYPHEN→UNDERSCORE NORMALISATION IS GONE, 2026-08-22, WITH THE ONLY KEY IT EVER SERVED.
 * It read `.replace(/-/g, '_')` so `/model-inventory` and `/model_inventory` were one key.
 * `model_inventory` was the sole underscored member of this allowlist and it has been FULL-DELETED
 * with the mock page it named; every surviving key (`analytics`, `models`, `register`,
 * `dash`) is a single unhyphenated word, so the transform could only ever have mapped a hyphenated
 * path onto a key that is not in this map — i.e. `null` either way. It was inert, not defensive.
 * A future underscored key must re-add it AND a test, rather than inherit an untestable transform.
 *
 * ONLY a single-segment path resolves. A deeper path is not a top-level view, and matching on
 * its first segment would claim deep links this router cannot honour: per-model sub-coordinates
 * are query params, not path segments, so `/models/mdl_123/projects` must resolve to nothing
 * rather than silently landing the user on the model list with the rest of the URL discarded.
 */
export function viewKeyFromUrl(params: URLSearchParams): string | null {
  const explicit = params.get('view');
  if (explicit) return explicit;
  return viewKeyFromPathname(window.location.pathname);
}

/**
 * The PATH half of the read above, split out so the WRITER can consult it too.
 *
 * <p><b>🔴 WHY IT IS SEPARATE, 2026-08-23.</b> `urlForView` never touched the pathname, so the
 * key-less default branch — the only branch that writes no `?view=` key — left an allowlisted path
 * in the address bar naming a DIFFERENT view than the one on screen. Navigating to the default from
 * `/models` produced the URL `/models`, which this ladder resolves straight back to the model LIST:
 * the page said one thing, the address bar and the nav highlight said another, and Back/Forward
 * onto that entry rendered the other one. Every other branch is immune because `?view=` (or
 * `workspaceId`) wins over the path — the key-less branch is immune to nothing, because it
 * deliberately writes neither.</p>
 *
 * <p>⚠️ <b>CORRECTED 2026-08-23 (same day):</b> this paragraph named that branch
 * <s><code>workspaces</code></s> and its worked example was "navigating to Projects". That view is
 * <b>deleted</b> — the landing is now the model list — so the branch is no longer reached by any
 * variant and is retained only as the writer's totality guard. <b>The hazard it describes is
 * unchanged and still real</b> for any future key-less view, which is why the paragraph is
 * corrected rather than removed.</p>
 *
 * <p>⚠️ It takes the pathname as an ARGUMENT rather than reading `window`, because `urlForView` is
 * pure by contract: it is handed an href and touches no globals. A second copy of the allowlist
 * lookup inside the writer would be the drift this module exists to prevent, so the writer calls
 * THIS — the same allowlist, the same casing rule, the same single-segment rule.</p>
 */
export function viewKeyFromPathname(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length !== 1) return null;
  return VIEW_KEY_BY_PATH[segments[0].toLowerCase()] ?? null;
}


/**
 * The URL-BEARING shape of a top-level view — the subset of `PrdView` that `urlForView` reads.
 *
 * ⚠️ WHY A SECOND UNION RATHER THAN IMPORTING `PrdView`. `PrdView` carries `workspace?: Workspace`,
 * which pulls `../app/contracts/domain` into whatever imports it. This module is a pure function of
 * the URL with no React and no view imports — that is the reason it exists as its own file — and
 * `PrdApp` already imports FROM here, so importing `PrdView` back would close a cycle.
 *
 * ⚠️ AND WHY THE DUPLICATION IS SAFE: it is DERIVE-CHECKED, not hoped-for.
 * `apps/test-scripts/frontend/router/dispatchLadderParity.test.ts` harvests the members of THIS union
 * from source alongside `PrdView` and both dispatch ladders, and fails if the four sets diverge. A
 * hand-kept second copy would be the "tenth copy wearing a guard's name" shape; a copy with a derived
 * equality check is a narrowing with a guard on it.
 *
 * ⚠️ AMENDED 2026-08-29 — operator ruling R-Q15 (FULL REMOVAL of the magic-link plane). A member
 * `access-expired`, carrying an optional `reason`, stood at the end of this union and had a matching
 * arm in BOTH ladders below. It named the terminal page a magic-link cookie's failure routed to; that
 * page, its callers and its error codes are all FULL-DELETED, so the member and both arms go with
 * them. The derive-check above is what makes this a three-file edit rather than a four-file one: drop
 * the member here without dropping `PrdView`'s and the parity test fails rather than the drift
 * shipping.
 */
export type UrlBearingView =
  // ➕ `W-14` step 2 (2026-09-05) — `create` and `exportJob`: THE TOOLBOX FRONT DOOR'S HAND-OFF.
  //
  // 🔴 THE VARIANT WAS EXACTLY `{type, modelId, tab?}`, AND THAT WAS BLOCKER (1) OF THREE. The
  // export modal's "Create a new project" affordance was a hardcoded
  // `window.location.assign('/?view=models')`; even with the parent model in hand it had NO KEY to
  // put the intent in, because `urlForView` strips `?model=`/`?tab=` on every non-model branch and
  // no create-intent key existed in the allowlist at all. Blocker (2) was that the auto-open
  // mechanism (`pendingCreate` → `initialCreateServiceType`) is in-page React state, which a
  // full-page `assign` structurally cannot reach. This widening closes (1) so (2) has a carrier.
  //
  // ⚠️ WIDENED, NOT FAKED — the caller is a FIRST-CLASS CASE, per core-principles. There is no
  // synthetic model id and no placeholder service type: a caller with no parent model in hand
  // produces neither param and keeps the model list as its stated destination. (⚠️ the standalone
  // Toolbox mount was that caller until 2026-09-08; ruling `R-C3` full-deleted it, and the surviving
  // Toolbox is the model tab, which always has its model.)
  //
  // ⚠️ AND `ModelHomePage`'s *"⚠️ IT IS NOT PUT IN THE URL, DELIBERATELY"* STILL HOLDS. That note
  // is about the intent as a NAVIGATION COORDINATE — somewhere you can be, shareable, re-enterable
  // — and this is not that. It is a one-shot HAND-OFF: `PrdApp` reads it at the mount hop, forwards
  // it into the view, and strips it with `replaceState` (no history entry), which is byte-for-byte
  // how `?afterImport=1` has always been handled. Consumed once, it cannot re-arm on Back or on a
  // refresh, which is the harm the note names.
  | { type: 'model'; modelId: string; tab?: string; create?: string; exportJob?: string }
  | { type: 'models' }
  | { type: 'register' }
  // `originModelId`: the model whose Projects tab this takeover was ENTERED from, when it was.
  // URL-BEARING on purpose — see the write branch. Absent for the legacy flat-list entry point.
  | { type: 'workspace'; workspaceId: string; originModelId?: string }
  | { type: 'clear' }
  | { type: 'analytics' };

/**
 * The view → URL contract: the exact URL `PrdApp` pushes for a view. PURE — it takes the current
 * href and returns a string, touching neither `window` nor history.
 *
 * <p>WHY IT IS HERE AND NOT INLINE IN THE COMPONENT. It is the WRITE half of the same contract
 * `viewKeyFromUrl` owns the READ half of, and this module's header already gives the reason for
 * both: the two must agree, and a mapping that lives inside a `useCallback` cannot be tested at
 * all. WI-24 criterion 1 requires router tests "covering both ladders AND `setUrlView`" — the
 * writer was the half that could not be covered, so it moved.</p>
 *
 * <p>Behaviour is a byte-faithful lift of the previous inline implementation; the only change is
 * that the caller now supplies the href and performs the `pushState`.</p>
 */
export function urlForView(next: UrlBearingView, currentHref: string): string {
  const url = new URL(currentHref);
  // URL param ownership (Cluster E/fe-conventions-6, WI-24 criterion 3): `?model=` and `?tab=` are
  // OWNED by the model branch and stripped by every other, so they can never leak across surfaces.
  //
  // ⚠️ ONE guarded block per OWNER, deliberately — per-branch deletes are how a NEW branch silently
  // leaks the params it forgot to clear. The property that matters is that a branch added later
  // strips by DEFAULT and has to opt in to owning a param.
  //
  // ⛔ THE `ws`/`case`/`stage` BLOCK THAT STOOD HERE IS GONE (2026-08-22). It was the ORIGINAL
  // instance of this pattern — the "reggie-param precedent" the criterion-3 wording above cited —
  // and it was deleted with the `?view=reggie` route it served. Nothing writes those three params
  // any more; a stale bookmark carrying them resolves to the default view and they are simply
  // ignored. The pattern survives here, in the branch that copied it.
  //
  // 🔴 `?model=` HAS A SECOND WRITER AS OF 2026-08-23, AND THE RULE ABOVE IS UNCHANGED BY IT.
  // A project takeover reached FROM a model homepage carries that model's id so its one back
  // control can return to the model's Projects tab — the locked mock's `← All projects`. It lives
  // in the URL rather than in component state because the control must survive the two things
  // component state does not: a refresh and a Back/Forward, both of which re-resolve the view
  // from the URL alone.
  //
  // ⚠️ THE STRIP BELOW IS STILL UNCONDITIONAL FOR EVERY NON-MODEL BRANCH, AND THAT IS DELIBERATE
  // RATHER THAN AN OVERSIGHT — it was written as an ownership predicate first, and MUTATION
  // TESTING SHOWED THE PREDICATE WAS DEAD: the workspace branch re-SETS the param immediately
  // after, so strip-then-set and don't-strip-then-set are the same URL, and disabling the
  // workspace arm of the predicate survived every assertion. Strip-by-default + an explicit write
  // is the shape that keeps its teeth: a branch added later inherits the strip and has to opt in
  // by writing the param, which is exactly the property the note above is about.
  //
  // ⚠️ `?tab=` GAINED NO WRITER. It is the model homepage's own coordinate; the takeover's back
  // destination is decided by the back handler (always Projects), not inherited from wherever the
  // user happened to be. So a workspace URL carrying a stale `?tab=` still has it stripped.
  //
  // ➕ `W-14` — `?create=` and `?exportJob=` JOIN THE SAME OWNED BLOCK. They are the model branch's
  // params for the same reason `?tab=` is: a create intent names a model, and a bundle destined for
  // a project under that model is meaningless anywhere else. Stripped by default here so a hand-off
  // can never leak onto a workspace, the toolbox or the dashboard.
  if (next.type !== 'model') {
    url.searchParams.delete('model');
    url.searchParams.delete('tab');
    url.searchParams.delete('create');
    url.searchParams.delete('exportJob');
  }
  // Keep URL stable across refresh so we don't "kick out" of workspace view.
  if (next.type === 'workspace') {
    url.searchParams.set('workspaceId', next.workspaceId);
    url.searchParams.delete('view');
    // SET after the strip, so a takeover entered from model B cannot inherit model A's id off a
    // stale URL: absent origin ⇒ the param stays deleted, and there is no `else` to write.
    if (next.originModelId) url.searchParams.set('model', next.originModelId);
  } else if (next.type === 'model') {
    url.searchParams.delete('workspaceId');
    url.searchParams.set('view', 'model');
    // Unconditional, unlike the optional `?tab=` below: a model homepage without a model is
    // not a view. `modelId` is REQUIRED on the variant, so there is no absent case to handle here —
    // and the read ladders refuse `?view=model` with no `?model=` rather than resolving to a page
    // that would have nothing to render.
    url.searchParams.set('model', next.modelId);
    // The model homepage's tab strip is URL-addressable, for the same reason every other view is:
    // a tab held only in component state loses itself on refresh and on Back. `tab` is OWNED by
    // this branch and stripped by every other — the guarded block below, same shape as `model`.
    if (next.tab) url.searchParams.set('tab', next.tab); else url.searchParams.delete('tab');
    // ➕ `W-14` — WRITTEN WHEN CARRIED, DELETED WHEN NOT, exactly like `?tab=` above and for a
    // sharper version of the same reason. A model branch that INHERITED a stale `?create=` off the
    // previous URL would re-open the create dialog on an ordinary later visit to the model — the
    // precise harm `pendingCreate`'s docblock promises against ("a later plain visit to the tab does
    // not re-open it"). There is no `else` writing a default: an absent intent is absent.
    if (next.create) url.searchParams.set('create', next.create); else url.searchParams.delete('create');
    if (next.exportJob) url.searchParams.set('exportJob', next.exportJob); else url.searchParams.delete('exportJob');
  } else if (next.type === 'models') {
    url.searchParams.delete('workspaceId');
    url.searchParams.set('view', 'models');
  } else if (next.type === 'register') {
    // Registration is a top-level SCREEN in the mock, not a dialog over the list — it owns the
    // whole viewport and has its own back path. So it is URL-addressable like any other view, and
    // it carries no params of its own: everything it collects is typed on the screen.
    url.searchParams.delete('workspaceId');
    url.searchParams.set('view', 'register');
  } else if (next.type === 'clear') {
    url.searchParams.delete('workspaceId');
    url.searchParams.set('view', 'clear');
  } else if (next.type === 'analytics') {
    url.searchParams.delete('workspaceId');
    url.searchParams.set('view', 'analytics');
  } else {
    // ⚠️ 2026-08-23: with `workspaces` deleted, NO variant of `UrlBearingView` reaches this arm —
    // every remaining one writes an explicit `?view=` (or `workspaceId`). It is kept, rather than
    // removed as dead code, because it is the writer's TOTALITY guard: a variant added later that
    // forgets its own arm lands here and gets a URL that still resolves, instead of silently
    // inheriting whatever `?view=` the previous view left behind. The path neutralisation below is
    // what makes that safe, and it is the reason this cannot simply be dropped.
    url.searchParams.delete('workspaceId');
    url.searchParams.delete('view');
    // 🔴 THE PATHNAME, AND THIS IS THE ONE BRANCH THAT HAS TO CARE. Every branch above writes a
    // `?view=` key (or `workspaceId`), and the read ladder gives those precedence over the path —
    // so a leftover `/models` is inert for them. This branch writes NEITHER, by design: the
    // default view IS the absence of a key. Leaving an allowlisted path behind therefore produced
    // a URL that resolves to a different view than the one just rendered, and Back onto that entry
    // rendered the other one. Neutralised to `/` — the form `?view=`-less URLs are written in.
    //
    // ⚠️ ONLY when the path NAMES a view. An unrecognised path already resolves to this same
    // default, so rewriting it would change a URL without fixing a disagreement — and this router
    // does not own every path the SPA fallback serves.
    if (viewKeyFromPathname(url.pathname)) url.pathname = '/';
  }
  return url.toString();
}

/**
 * The URL → VIEW contract: the ONE read ladder.
 *
 * <p><b>🔴 WHY THIS EXISTS, AND WHY IT IS A DELETION RATHER THAN AN ADDITION.</b> `PrdApp` carried
 * TWO hand-maintained read ladders — a mount-time effect and a `popstate` handler — and this
 * module's own header has said since it was created that they "must resolve a URL identically. Two
 * copies of this ladder is how a fresh load and a Back button came to disagree." Extracting
 * `viewKeyFromUrl` unified the URL→KEY step; the KEY→VIEW step stayed duplicated, and the guard
 * written to watch it could not actually see the drift.</p>
 *
 * <p><b>MEASURED, 2026-08-23.</b> Mutation testing of that guard: disabling the `model` branch in
 * EITHER ladder — leaving the `setView({ type: 'model', … })` call present but unreachable —
 * SURVIVED every assertion. The guard harvested the SETTER, not the CONDITION, so "this type is
 * mentioned" stood in for "this type is reachable". That hole was not specific to `model`; it
 * applied to every view type, and it had been there since the guard was written.</p>
 *
 * <p><b>Eliminating a divergence by construction beats testing its consequences one at a time.</b>
 * There is now one ladder, it is pure, and it is executed by the round-trip tests rather than
 * pattern-matched.</p>
 *
 * <p>⚠️ It returns `UrlBearingView`, NOT `PrdView` — see that type's note on the import cycle. The
 * component layers on the two fields that are not URL-derived (`workspace`,
 * `autoOpenEnrichment`).</p>
 */
export function resolveViewFromUrl(params: URLSearchParams): UrlBearingView {
  // `workspaceId` wins over `?view=`, and that precedence is load-bearing rather than incidental:
  // a workspace is addressed by its param and writes NO view key, so a stale `?view=` left on the
  // URL must not shadow it.
  const workspaceId = params.get('workspaceId');
  if (workspaceId) {
    // The takeover's ORIGIN, when the URL carries one — see the write branch. Read here rather
    // than kept in component state so a refresh and a Back both recover the same back-destination
    // a fresh click would have produced. An empty `?model=` is falsy and therefore absent, the
    // same rule the model branch below relies on.
    const originModelId = params.get('model');
    if (originModelId) return { type: 'workspace', workspaceId, originModelId };
    return { type: 'workspace', workspaceId };
  }

  const key = viewKeyFromUrl(params);
  const modelId = params.get('model');
  // `?view=model` with no id resolves to the DEFAULT, not to a model view. A model homepage with no
  // model has nothing to render, and mounting it would be a blank page rather than an error.
  if (key === 'model' && modelId) {
    return {
      type: 'model',
      modelId,
      tab: params.get('tab') ?? undefined,
      // ➕ `W-14` — the hand-off, read back. `|| undefined` rather than `?? undefined`: an EMPTY
      // `?create=` must read as ABSENT, not as an empty service type. `R47` says the type comes
      // from which button was pressed, so a type-less create dialog is a state the product does not
      // have — and an empty `exportJob` would send the park an id it does not hold, which is the
      // "never invent a value the caller does not have" antipattern this whole axis was built on.
      // Same rule the `&& modelId` guard above already applies to an empty `?model=`.
      create: params.get('create') || undefined,
      exportJob: params.get('exportJob') || undefined,
    };
  }
  if (key === 'models') return { type: 'models' };
  if (key === 'register') return { type: 'register' };
  if (key === 'clear') return { type: 'clear' };
  // `dash` is the LOCKED MOCK's own coordinate for this screen and `analytics` is the shipped one.
  // Both resolve; only `analytics` is ever WRITTEN (see the write branch), so no existing deep link
  // and no `postLoginReturnTarget` changes, and typing the mock's URL does not land on Projects.
  if (key === 'analytics' || key === 'dash') return { type: 'analytics' };
  // ⚠️ AMENDED 2026-08-29 (ruling R-Q15): an `access-expired` arm stood here, reading `?reason=` off
  // the URL. ⚠️ Written WITHOUT the `type:` punctuation, for the same reason the `workspaces` note
  // below is — this function's source is harvested and a comment carrying that shape is counted as a
  // live ROUTE. Its page is FULL-DELETED with the magic-link plane, so a stale bookmark carrying
  // `?view=access-expired` now falls through to the terminal below and lands on the model list.
  // TERMINAL. An unrecognised URL RESOLVES to the MODEL LIST; it does not fall through leaving the
  // caller to decide what "no answer" meant. The mount ladder used to have no such branch.
  //
  // 🔴 OPERATOR RULING 2026-08-23 — THE LANDING IS THE MODEL LIST, AND THIS LINE WAS THE DRIFT.
  // It returned the `workspaces` variant: the LEGACY flat list of every project in the deployment,
  // ⚠️ written WITHOUT the `type:` punctuation on purpose — `dispatchLadderParity.test.ts` harvests
  // this function's source with /type:\s*'([a-z_-]+)'/ to prove the read ladder covers the union,
  // and a comment carrying that shape is counted as a ROUTE. Prose in a harvested region is data.
  // which the concept doc draws as the BEFORE state and labels in its own words "One flat list …
  // which is the whole problem, expressed as a screen"
  // (`cycle 5/model-registry/models-above-workspaces.html`, the wireframe note above
  // "Target — landing"). So the default landing was the screen the whole cycle exists to replace,
  // and it was not even reachable from the nav — `SideRail`'s `NAV_ITEMS` is `analytics` + `models`.
  // Projects are reached UNDER their model, via `ModelHomePage`'s Projects tab (`ModelProjects`,
  // the mock's `s-projects`), which is what "folded in under their corresponding model" means.
  return { type: 'models' };
}

/**
 * The model homepage tab a project takeover returns TO.
 *
 * ⚠️ A LITERAL, NOT AN IMPORT, and the reason is this module's whole premise: it is a pure function
 * of the URL with no view imports, so importing `MODEL_TABS` from `views/model/ModelHomePage` would
 * pull the page — and its lazy chunk — into the router's eager graph. It is DERIVE-CHECKED instead:
 * `apps/test-scripts/frontend/router/urlForView.test.ts` harvests `MODEL_TABS` from that file and
 * fails if this value is not a member. That check earns its place because the failure it catches is
 * SILENT — `resolveTab` maps an unknown tab to Overview, so a typo here would land the user on the
 * wrong tab with no error anywhere.
 */
const TAKEOVER_RETURN_TAB = 'projects';

/**
 * Where the project-takeover's ONE back control goes.
 *
 * <p><b>🔴 WHY IT IS A FUNCTION IN THIS MODULE AND NOT A TERNARY IN THE JSX.</b> Same reason
 * `urlForView` is: a navigation decision written inline in a `<SideRail onBack=…>` prop cannot be
 * tested at all, and this one was WRONG for the registry's highest-traffic path — it dispatched
 * `{ type: 'workspaces' }` unconditionally, so model → Projects → open a project → back landed the
 * user on the LEGACY flat list of every project in the deployment. The locked mock (rev 5) draws
 * this control on screen <code>s-projectws</code> as <code>← All projects</code> with
 * <code>data-goto="projects"</code>, which resolves to <code>s-projects</code> — the model
 * homepage's Projects tab.</p>
 *
 * <p>⚠️ THE ABSENT ARM IS A DESTINATION, NOT A FALLBACK — and 2026-08-23 CHANGED WHAT THAT
 * DESTINATION IS, without changing why the arm exists. It used to return the legacy flat list,
 * which was that path's correct answer while the list existed. <b>The list is deleted</b> (operator
 * ruling, and locked-mock rule <code>R55</code>), so its correct answer is now the MODEL LIST — the
 * new landing. This is still not a fallback and still not an invented model id: a takeover reached
 * by a bare <code>?workspaceId=</code> deep link genuinely has no model origin, and sending it to
 * the tier above is a stated destination for that case, not a degraded version of the other arm.</p>
 */
export function backViewFromTakeover(originModelId: string | undefined): UrlBearingView {
  if (originModelId) return { type: 'model', modelId: originModelId, tab: TAKEOVER_RETURN_TAB };
  return { type: 'models' };
}

/**
 * Where the MODEL HOMEPAGE's one back control goes — the model list.
 *
 * <p><b>🔴 WI-88 (operator walk 2026-09-02, item 2). THERE WAS NO WAY BACK.</b> `PrdApp` gave the
 * model branch <code>onBack={undefined}</code>, so the shell's back control — which it
 * builds and has always built — was never handed a handler on the one branch that needed it. The
 * only exit was the top-nav <code>Models</code> tab, which `NAV_BY_VIEW` paints "you are here", so
 * the single available return path was the one control that looks like it does nothing.</p>
 *
 * <p><b>🔴 WHY THIS IS A FUNCTION RATHER THAN A LITERAL IN THE JSX PROP.</b> Identical reason to
 * {@link backViewFromTakeover} directly above, whose docblock records what it cost to learn: a
 * navigation decision written inline in a `<SideRail onBack=…>` prop cannot be tested at all, and
 * that one shipped WRONG for the registry's highest-traffic path. A nullary function returning a
 * constant still buys the thing that mattered — the destination is named, exported, asserted by
 * `apps/test-scripts/frontend/router/urlForView.test.ts`, and guarded against re-inlining by the
 * source-harvesting case in `dispatchLadderParity.test.ts`.</p>
 *
 * <p>⚠️ <b>IT TAKES NO ARGUMENT, AND THAT IS THE ANSWER RATHER THAN AN OMISSION.</b> The sibling
 * takes the takeover's origin because a takeover has a tier below it to return to. A model homepage
 * does not: every model, on every one of its five tabs, sits directly under the list. Threading
 * `view.tab` in so the signature looked richer would be an input the destination does not depend on.</p>
 *
 * <p>⚠️ <b>THE STALE-PARAM HALF BELONGS TO `urlForView`, NOT HERE.</b> `{ type: 'models' }` is a
 * non-model branch, so the unconditional strip at the top of `urlForView` deletes `?model=` and
 * `?tab=`. WI-88 closure criterion 1 asserts that end-to-end (write → read back) rather than
 * assuming it from this side.</p>
 *
 * <p>📌 <b>DATED AMENDMENT TO THE LOCKED DRAWING — 2026-09-02, WI-88.</b> The operator asked for a
 * <b>back button</b>. The locked mock (rev 5) draws a <b>breadcrumb link</b> instead: `PAGE.detail`
 * … `PAGE.access` all declare <code>crumb:'Models'</code>
 * (`cycle 5/model-registry/registry-fe-views.html:6223-6227`) and `renderToolbar` emits it as
 * <code>&lt;a href="#" data-goto="list"&gt;Models&lt;/a&gt;</code> (`:6270`). ⛔ <b>The mock does NOT
 * specify a back button and must never be cited as if it did.</b> Both discharge the same need; the
 * operator's literal ask was built because it reuses a control the shell already renders and needs no
 * new prop on `ModelHomePage`. Recorded here, at `PrdApp`'s `onBack` site, at `ModelHomePage`'s
 * still-empty `breadcrumb` slot, and at WI-88 in
 * `cycle 5/model-registry/model-registry-2026-08-14.md`.</p>
 */
export function backViewFromModelHome(): UrlBearingView {
  return { type: 'models' };
}
